import { DATE_FORMATS } from "@booking/consts";
import clsx from "clsx";
import {
  endOfDay,
  format as formatDate,
  formatISO,
  startOfDay,
} from "date-fns";
import React from "react";

import type { CommonProps } from "@/types";

function areDatesUnique(rounds: Array<any>) {
  const dates = rounds.map(({ date }) => formatDate(date, "yyyy-MM-dd"));

  return dates.length === new Set(dates).size;
}

function camelize(str: string): string {
  return str.toLowerCase().replace(/[-|_]./g, ([_, s]) => s.toUpperCase());
}

function dayInterval(date: Date) {
  const start = startOfDay(date);
  const end = endOfDay(date);

  return { start, end };
}

function filterEntries(
  obj: Record<any, any>,
  fn: (arr: [any, any]) => boolean,
): Record<any, any> {
  return Object.fromEntries(Object.entries(obj).filter(fn));
}

function flatMapEntries(
  obj: Record<any, any>,
  fn: (arr: [any, any]) => Array<[any, any]>,
): Record<any, any> {
  return Object.fromEntries(Object.entries(obj).flatMap(fn));
}

function getEnumKeyByEnumValue<
  TEnumKey extends string,
  TEnumVal extends string | number,
>(enumObject: { [key in TEnumKey]: TEnumVal }, enumValue: TEnumVal) {
  const keys = (Object.keys(enumObject) as Array<TEnumKey>).filter(
    (x) => enumObject[x] === enumValue,
  );

  return keys.length > 0 ? keys[0] : undefined;
}

function groupBy<T>(arr: Array<T>, fn: (item: T) => any) {
  return arr.reduce(
    (groups, curr) => {
      (groups[fn(curr)] ||= []).push(curr);

      return groups;
    },
    {} as Record<string, Array<T>>,
  );
}

function isoDate(date: Date): string {
  return formatISO(date, { representation: "date" });
}

function mapEntries(
  obj: Record<any, any>,
  fn: (arr: [any, any]) => [any, any],
): Record<any, any> {
  return Object.fromEntries(Object.entries(obj).map(fn));
}

const ordinalDayShortMonth = (date: Date) =>
  formatDate(date, DATE_FORMATS.ORDINAL_DAY_SHORT_MONTH);

function renderChildrenWithProps(
  id: string,
  children: React.ReactNode,
  props: any,
) {
  const items = React.Children.toArray(children);

  if (items.length === 0) {
    return null;
  }

  return items
    .map((child, index) => {
      if (child == null) {
        return child;
      }

      const className = clsx(
        props.className,
        (child as React.ReactElement<CommonProps>).props.className,
      );

      return (
        <React.Fragment key={`${id}_${index}`}>
          {React.cloneElement(child as React.ReactElement, {
            ...props,
            className,
          })}
        </React.Fragment>
      );
    })
    .filter(Boolean);
}

function selectedDatesToString(selectedDates: Array<Date>) {
  return selectedDates.map((date) => ordinalDayShortMonth(date)).join(" - ");
}

function underscore(str: string): string {
  return str
    .split(/(?=[A-Z])/)
    .join("_")
    .toLowerCase();
}

function underscoreKeys(obj: object): object {
  return mapEntries(obj, ([k, v]) => {
    let value = v;

    if (Array.isArray(v)) {
      value = v.map(underscoreKeys);
    } else if (typeof v === "object") {
      value = underscoreKeys(v);
    }

    return [underscore(k), value];
  });
}

export {
  areDatesUnique,
  camelize,
  dayInterval,
  filterEntries,
  flatMapEntries,
  getEnumKeyByEnumValue,
  groupBy,
  isoDate,
  mapEntries,
  ordinalDayShortMonth,
  renderChildrenWithProps,
  selectedDatesToString,
  underscore,
  underscoreKeys,
};
